import React, { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { translations, navItems } from "../i18n/translations";

export default function Navigation() {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => setLang(lang === "en" ? "es" : "en");

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#F5F0E8]/95 backdrop-blur-md py-4 border-b border-[#E0DACE]"
            : "bg-transparent py-7"
        }`}
      >
        <nav className="max-w-[1500px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <a href="#inicio" className="flex flex-col items-start group">
            <span
              className={`font-serif tracking-[0.3em] text-xl lg:text-[22px] transition-colors duration-500 ${
                scrolled ? "text-[#2A2A28]" : "text-white"
              }`}
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              N A C U I I
            </span>
            <span
              className={`text-[10px] tracking-[0.4em] mt-1 transition-colors duration-500 ${
                scrolled ? "text-[#9C8456]" : "text-white/80"
              }`}
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              TODOS  SANTOS
            </span>
          </a>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navItems.map((item) => (
              <li key={item.key}>
                <a
                  href={item.href}
                  className={`text-[11px] xl:text-[12px] tracking-[0.18em] xl:tracking-[0.22em] uppercase transition-colors duration-300 hover:opacity-70 ${
                    scrolled ? "text-[#2A2A28]" : "text-white"
                  }`}
                  style={{ fontFamily: "'Jost', sans-serif", fontWeight: 400 }}
                >
                  {t(translations.nav[item.key])}
                </a>
              </li>
            ))}
          </ul>

          {/* Right side: language toggle + CTA */}
          <div className="hidden lg:flex items-center gap-5">
            <button
              onClick={toggleLang}
              aria-label="Toggle language"
              className={`flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase transition-colors duration-300 hover:opacity-80 ${
                scrolled ? "text-[#2A2A28]" : "text-white"
              }`}
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 500 }}
            >
              <Globe className="w-4 h-4" strokeWidth={1.4} />
              <span className={lang === "en" ? "opacity-100" : "opacity-50"}>EN</span>
              <span className="opacity-40">/</span>
              <span className={lang === "es" ? "opacity-100" : "opacity-50"}>ES</span>
            </button>

            <a
              href="#contacto"
              className={`inline-flex items-center justify-center px-7 py-3 text-[11px] tracking-[0.25em] uppercase border transition-all duration-300 ${
                scrolled
                  ? "border-[#2A2A28] text-[#2A2A28] hover:bg-[#2A2A28] hover:text-[#F5F0E8]"
                  : "border-white/80 text-white hover:bg-white hover:text-[#2A2A28]"
              }`}
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(translations.nav.bookVisit)}
            </a>
          </div>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-4">
            <button
              onClick={toggleLang}
              aria-label="Toggle language"
              className={`text-[11px] tracking-[0.3em] uppercase ${
                scrolled ? "text-[#2A2A28]" : "text-white"
              }`}
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 500 }}
            >
              {lang.toUpperCase()}
            </button>
            <button
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className={scrolled ? "text-[#2A2A28]" : "text-white"}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] bg-[#F5F0E8] transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        } lg:hidden`}
      >
        <div className="flex justify-between items-center px-6 py-7 border-b border-[#E0DACE]">
          <span
            className="font-serif tracking-[0.3em] text-xl text-[#2A2A28]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            N A C U I I
          </span>
          <button onClick={() => setOpen(false)} aria-label="Close menu">
            <X className="w-6 h-6 text-[#2A2A28]" />
          </button>
        </div>
        <ul className="flex flex-col items-center justify-center gap-7 mt-14">
          {navItems.map((item) => (
            <li key={item.key}>
              <a
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-[14px] tracking-[0.25em] uppercase text-[#2A2A28]"
                style={{ fontFamily: "'Jost', sans-serif" }}
              >
                {t(translations.nav[item.key])}
              </a>
            </li>
          ))}
          <li>
            <a
              href="#contacto"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center px-8 py-3 mt-4 text-[12px] tracking-[0.25em] uppercase border border-[#2A2A28] text-[#2A2A28]"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              {t(translations.nav.bookVisit)}
            </a>
          </li>
        </ul>
      </div>
    </>
  );
}
